import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ApproveInvoiceWizard from "@/pages/dashboard/fs/wizards/ApproveInvoice";
import { Loader2, CircleDollarSign, Download, Copy } from "lucide-react";
import { V } from "@/lib/vocab";

const usd = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (s) => (s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");
const PILL = {
  submitted: ["bg-amber-50 text-amber-800", "To review"],
  board_review: ["bg-indigo-50 text-indigo-700", "With the board"],
  approved: ["bg-emerald-50 text-emerald-700", "Approved"],
  exported: ["bg-emerald-50 text-emerald-700", "Sent to accounting"],
  paid: ["bg-sand-200 text-ink/60", "Paid"],
  rejected: ["bg-red-50 text-red-700", "Not approved"],
};

export default function Invoices({ tenantId, readOnly, canDecide }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tenants/${tenantId}/field-services/invoices`, { params: { status: filter } });
    setD(data);
  }, [tenantId, filter]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load the invoices")); }, [load]);

  if (!d) return <div className="grid h-48 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  const c = d.counts || {}; const t = d.totals || {};

  const exportBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/invoices/export`, {});
      const blob = new Blob([data.csv], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `vendor-invoices-${data.batch.id}.csv`;
      a.click();
      toast.success(`${data.batch.count} invoice(s) exported${data.batch.emailed_to ? ` and emailed to ${data.batch.emailed_to}` : ""}`);
      await load();
    } catch (e) { toast.error(errMsg(e, "Nothing to export")); }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="fs-invoices">
      <p className="rounded-xl bg-sand-100 px-4 py-3 text-xs leading-relaxed text-ink/65">
        <b>Redline never moves money.</b> It captures the invoice, checks it against what was agreed and
        what was actually done, codes it, gets it approved, and hands accounting a batch. You mark it paid
        once your system has paid it.
      </p>

      <div className="grid gap-2 sm:grid-cols-4">
        {[["To review", c.submitted || 0, t.submitted], [`With the ${V.grp()}`, c.board_review || 0, t.board_review],
          ["Approved, unpaid", (c.approved || 0) + (c.exported || 0), (t.approved || 0) + (t.exported || 0)],
          ["Paid", c.paid || 0, t.paid]].map(([l, n, sum]) => (
          <div key={l} className="rounded-2xl border border-sand-200 bg-white px-4 py-3"
            data-testid={`inv-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-2xl font-extrabold text-ink">{n}</div>
            <div className="text-xs font-semibold text-ink/50">{l}</div>
            <div className="text-xs text-ink/40">{usd(sum || 0)}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["open", "To review"], ["unpaid", "Approved, unpaid"], ["paid", "Paid"], ["rejected", "Sent back"], ["", "Everything"]].map(([k, label]) => (
          <button key={k || "all"} data-testid={`inv-filter-${k || "all"}`} onClick={() => setFilter(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${filter === k
              ? "bg-ink text-white" : "bg-sand-100 text-ink/55 hover:bg-sand-200"}`}>{label}</button>
        ))}
        {!readOnly && (c.approved || 0) > 0 && (
          <button data-testid="inv-export" disabled={busy} onClick={exportBatch}
            className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
            <Download className="h-4 w-4" /> Export {c.approved} approved
          </button>
        )}
      </div>

      {!(d.invoices || []).length ? (
        <div data-testid="inv-empty" className="rounded-2xl border border-dashed border-sand-300 py-12 text-center">
          <CircleDollarSign className="mx-auto h-7 w-7 text-ink/20" />
          <p className="mt-2 text-sm text-ink/50">No invoices here.</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-ink/40">
            Vendors submit their invoices against an active work order from their own page — you never
            have to chase a PDF by email.
          </p>
        </div>
      ) : (d.invoices || []).map((i) => (
        <button key={i.id} data-testid={`inv-row-${i.id}`} onClick={() => setOpen(i.id)}
          className="flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border border-sand-200 bg-white px-4 py-3 text-left hover:border-sand-300">
          <div className="min-w-[12rem] flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${(PILL[i.status] || [])[0]}`}>
                {(PILL[i.status] || [])[1] || i.status}
              </span>
              <span className="text-[11px] font-bold uppercase text-ink/40">{i.type}</span>
              <span className="font-mono text-[11px] text-ink/35">{i.wo_ref}</span>
              {i.gl_code && <span className="text-[11px] font-semibold text-ink/45">code {i.gl_code}</span>}
            </div>
            <div className="mt-0.5 truncate text-sm font-semibold text-ink">{i.vendor_name} · {i.wo_title}</div>
            <div className="text-xs text-ink/45">{when(i.submitted_at)}{i.number ? ` · #${i.number}` : ""}</div>
          </div>
          <div className="shrink-0 text-lg font-extrabold text-ink">{usd(i.amount)}</div>
        </button>
      ))}

      {(d.batches || []).length > 0 && (
        <div data-testid="inv-batches">
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Export batches</div>
          {d.batches.map((b) => (
            <div key={b.id} className="mb-1.5 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sand-200 bg-white px-4 py-2.5 text-sm">
              <span className="text-ink/70">
                {when(b.created_at)} · {b.count} invoice(s) · {usd(b.total)}
                {b.emailed_to ? ` · emailed to ${b.emailed_to}` : ""}
              </span>
              <span className="flex gap-2">
                <a href={`/api/tenants/${tenantId}/field-services/invoice-batches/${b.id}?fmt=csv`}
                  data-testid={`batch-csv-${b.id}`} className="text-xs font-bold text-ink/60 underline">CSV</a>
                <a href={`/api/tenants/${tenantId}/field-services/invoice-batches/${b.id}?fmt=qb`}
                  data-testid={`batch-qb-${b.id}`} className="text-xs font-bold text-ink/60 underline">QuickBooks</a>
                <button onClick={() => copyText(b.id, "Batch reference copied")}
                  className="text-ink/30 hover:text-ink/60"><Copy className="h-3.5 w-3.5" /></button>
              </span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Invoice review</DialogTitle></DialogHeader>
          {open && <ApproveInvoiceWizard tenantId={tenantId} iid={open} readOnly={readOnly}
            canDecide={canDecide} onChanged={load} onClose={() => setOpen(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
